/**
 * Layout Overlays Component
 *
 * Renders all shared overlay JSX (sheets + alerts) consumed by both DesktopLayout
 * and MobileLayout. Extracted to eliminate ~480 lines of duplication.
 *
 * Overlays rendered:
 * - Location Selector Sheet
 * - Delivery Address Flow Sheet (3-step: address, details, location)
 * - Auth Flow Sheet (7 screens: signin-phone, signin-email, enter-sms-code,
 *   enter-email-code, signin-password, reset-password, signup)
 * - Order Tracking Sheet (delivery + pickup variants)
 * - Delivery availability Alert
 * - Contact restaurant Alert
 */

import {
  useDeliveryStore,
  useCartStore,
  Sheet,
  SheetContentStoreLocation,
  SheetContentDeliveryAddress,
  SheetContentAddressDetails,
  SheetContentSignInPhone,
  SheetContentEnterSmsCode,
  SheetContentSignInPassword,
  SheetContentResetPassword,
  SheetContentSetNewPassword,
  SheetContentSignUp,
  SheetContentPickupOrderStatus,
  SheetContentDeliveryOrderStatus,
  MapOrderTracking,
  Alert,
  SheetContentFavoriteProduct,
  SheetContentReorderProduct,
} from '@restaurant/core'
import type { DeliveryAddress } from '@restaurant/core'
import { mapOrderStateToStatusFlow, getLocationForOrder } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'
import { mapFormToDropOff } from '@restaurant/viewmodels/sheets/AddressViewModel'
import type { LayoutOrchestration } from '../../hooks/useLayoutOrchestration'
import { useFavoriteProductSheet } from '../../hooks/useFavoriteProductSheet'
import { useReorderProductSheet } from '../../hooks/useReorderProductSheet'
import { StaticMapPreview } from './StaticMapPreview'
import { isShowcase } from '../../showcase/showcase'
import { showcaseBlocked } from '../../showcase/guard'

interface LayoutOverlaysProps {
  orchestration: LayoutOrchestration
}

export function LayoutOverlays({ orchestration: o }: LayoutOverlaysProps) {
  const favoriteSheet = useFavoriteProductSheet()
  const reorderSheet = useReorderProductSheet()

  return (
    <>
      {/* Location Selector Sheet */}
      <Sheet
        isOpen={o.isLocationSheetOpen}
        onClose={o.handleLocationSheetClose}
        title="Store Location"
        testID="location-selector-sheet"
      >
        <SheetContentStoreLocation
          locations={o.locationDataList}
          onLocationSelect={o.handleLocationSelect}
          selectedLocationId={o.storeLocation?.id}
          heading="Store Location"
          testID="location-selector-content"
        />
      </Sheet>

      {/* Delivery Address Flow Sheet */}
      <Sheet
        isOpen={o.isDeliveryAddressSheetOpen}
        onClose={() => {
          if (o.addressFlowStep === 'location') {
            o.handleDeliveryLocationStepClose()
          } else {
            o.handleDeliveryAddressSheetClose()
          }
        }}
        title={
          o.addressFlowStep === 'address' ? 'Delivery Address' :
          o.addressFlowStep === 'details' ? 'Address Details' :
          'Store Location'
        }
        testID="delivery-address-sheet"
      >
        {o.addressFlowStep === 'address' && (
          <SheetContentDeliveryAddress
            {...o.addressViewModel.getChangeDeliveryAddressProps()}
            title="Delivery Address"
            savedAddresses={o.badgedSavedAddresses}
            onSavedAddressSelect={async (address) => {
              // Showcase seeds two fake saved addresses so this list isn't
              // empty, but the visitor is unauthenticated with no
              // temporaryAddress — the branch below would otherwise resolve
              // fullAddress to undefined and bail silently, so the row reads
              // as broken instead of showing the book-a-call nudge.
              if (showcaseBlocked()) return

              const sheetControls = {
                close: () => o.resetAddressFlowState(),
                reopen: () => o.setIsDeliveryAddressSheetOpen(true),
              }

              // Resolve the full address object
              const fullAddress = o.isAuthenticated
                ? o.savedAddresses.find((a) => a.id === address.id)
                : o.temporaryAddress
                  ? {
                      id: 'temp-address',
                      userId: '',
                      businessId: o.currentRestaurant?.id ?? '',
                      street: o.temporaryAddress.street,
                      city: o.temporaryAddress.city,
                      state: o.temporaryAddress.state,
                      zipCode: o.temporaryAddress.zipCode,
                      latitude: o.temporaryAddress.latitude,
                      longitude: o.temporaryAddress.longitude,
                      lastUsed: new Date().toISOString(),
                      storeDeliveryFees: o.temporaryAddress.storeDeliveryFees ?? {},
                      apartment: o.temporaryAddress.apartment,
                      entryCode: o.temporaryAddress.entryCode,
                      buildingName: o.temporaryAddress.buildingName,
                      deliveryDetails: o.temporaryAddress.deliveryDetails,
                    } as DeliveryAddress
                  : undefined

              if (!fullAddress) return

              // Set address BEFORE candidate evaluation so the guard's fee
              // updates (including -1 sentinels for out-of-range) write to the
              // current address and aren't overwritten.
              o.setCurrentAddress(fullAddress)

              // Skip details — run candidate evaluation directly
              const result = await o.deliveryGuard.evaluateDeliveryCandidates(fullAddress, sheetControls.reopen)
              if (!result.passed) {
                // Alert shown by guard; close sheet if not transient
                if (!result.isTransientError) sheetControls.close()
                return
              }

              const { candidates } = result

              const singleCandidate = candidates[0]
              if (candidates.length === 1 && singleCandidate) {
                // Single location — auto-commit
                const locationId = singleCandidate.id
                o.setOrderType('Delivery')
                const deliveryState = useDeliveryStore.getState()
                const storedAddress = deliveryState.currentAddress
                const fee = storedAddress?.storeDeliveryFees?.[locationId] ?? fullAddress.storeDeliveryFees?.[locationId] ?? null
                if (fee !== null) useCartStore.getState().setDeliveryFee(fee)
                const matchedLocation = o.locationDataList.find((l) => l.id === locationId)
                if (matchedLocation) o.handleLocationSelect(matchedLocation)
                o.resetAddressFlowState()
                o.setAddressWasSelected(true)
              } else {
                // Multiple locations — go to location step and fetch progressively
                o.setAddressFlowStep('location')
                o.deliveryGuard.fetchRemainingFeesProgressively(fullAddress, candidates).catch(() => {})
              }
            }}
            onAddressSelect={async (address) => {
              const fullAddress = o.addressViewModel.searchResults.find((r) => r.id === address.id)
              if (!fullAddress) return
              o.addressViewModel.selectAddress(fullAddress)
              o.setAddressFlowStep('details')
            }}
            isCheckingFees={o.deliveryGuard.isCheckingFees}
            testID="delivery-address-content"
          />
        )}

        {o.addressFlowStep === 'details' && (
          <SheetContentAddressDetails
            {...o.addressViewModel.getAddressDetailsProps()}
            addressHeader={
              o.addressViewModel.selectedAddress
                ? {
                    addressLine1: o.addressViewModel.selectedAddress.addressLine1,
                    addressLine2: o.addressViewModel.selectedAddress.addressLine2,
                  }
                : { addressLine1: '', addressLine2: '' }
            }
            loading={o.addressViewModel.loading || o.deliveryGuard.isCheckingFees}
            mapComponent={
              <StaticMapPreview
                latitude={o.selectedLat}
                longitude={o.selectedLng}
                loading={o.staticMapLoading}
                error={o.staticMapError}
                url={o.staticMapUrl}
              />
            }
            onEditAddress={() => { o.setAddressFlowStep('address'); o.addressViewModel.goBackToSearch() }}
            onConfirmAddress={async () => {
              const selectedAddr = o.addressViewModel.selectedAddress
              if (!selectedAddr) return

              const pendingAddr = o.deliveryGuard.getPendingAddress()
              const fees = pendingAddr?.storeDeliveryFees ?? o.currentAddress?.storeDeliveryFees ?? {}

              const sheetControls = {
                close: () => o.resetAddressFlowState(),
                reopen: () => o.setIsDeliveryAddressSheetOpen(true),
              }

              let confirmedAddress: DeliveryAddress | undefined

              try {
                if (o.isAuthenticated) {
                  // Check if this is an existing saved address being edited
                  const existingSaved = o.savedAddresses.find((a) => a.id === selectedAddr.id)

                  if (existingSaved && !o.addressViewModel.hasDetailsChanged()) {
                    // No changes — just set as current
                    o.setCurrentAddress(existingSaved)
                    confirmedAddress = existingSaved
                  } else if (existingSaved) {
                    // Saved address with changes — update backend
                    const updates = {
                      apartment: o.addressViewModel.addressDetails.unitNumber || undefined,
                      entryCode: o.addressViewModel.addressDetails.entryCode || undefined,
                      buildingName: o.addressViewModel.addressDetails.buildingName || undefined,
                      deliveryDetails: {
                        dropOffOption: mapFormToDropOff(o.addressViewModel.addressDetails.deliveryPreference),
                        instructions: o.addressViewModel.addressDetails.deliveryNotes || undefined,
                      },
                      lastUsed: new Date().toISOString(),
                    }
                    await o.deliveryService.updateUserAddress(existingSaved.id, updates)
                    const updatedAddress: DeliveryAddress = {
                      ...existingSaved,
                      ...updates,
                      storeDeliveryFees: fees,
                    }
                    useDeliveryStore.getState().updateSavedAddress(existingSaved.id, updates)
                    o.setCurrentAddress(updatedAddress)
                    confirmedAddress = updatedAddress
                  } else {
                    // New autocomplete address — save to backend
                    // Use saveAddressDetails (not confirmAddress) to avoid
                    // the navigationService.goBack() that confirmAddress calls.
                    // The form-level deliveryPreference ('door' | 'hand') is in a
                    // different vocabulary than DeliveryAddressData's dropoff enum,
                    // so omit it here rather than spreading an incompatible value.
                    const { deliveryPreference: _formDeliveryPreference, ...addressDetailsForSave } =
                      o.addressViewModel.addressDetails
                    await o.addressViewModel.saveAddressDetails({
                      street: selectedAddr.street,
                      city: selectedAddr.city,
                      state: selectedAddr.state,
                      zipCode: selectedAddr.zipCode,
                      latitude: selectedAddr.latitude,
                      longitude: selectedAddr.longitude,
                      country: selectedAddr.country || 'US',
                      ...addressDetailsForSave,
                    })
                    const newConfirmedAddress: DeliveryAddress = {
                      id: selectedAddr.id,
                      userId: o.user?.id || '',
                      businessId: o.currentRestaurant?.id ?? '',
                      street: selectedAddr.street,
                      city: selectedAddr.city,
                      state: selectedAddr.state,
                      zipCode: selectedAddr.zipCode,
                      apartment: o.addressViewModel.addressDetails.unitNumber,
                      entryCode: o.addressViewModel.addressDetails.entryCode,
                      buildingName: o.addressViewModel.addressDetails.buildingName,
                      latitude: selectedAddr.latitude,
                      longitude: selectedAddr.longitude,
                      lastUsed: new Date().toISOString(),
                      deliveryDetails: {
                        dropOffOption: mapFormToDropOff(o.addressViewModel.addressDetails.deliveryPreference),
                        instructions: o.addressViewModel.addressDetails.deliveryNotes || undefined,
                      },
                      storeDeliveryFees: {},
                    }
                    o.setCurrentAddress(newConfirmedAddress)
                    confirmedAddress = newConfirmedAddress
                  }
                } else {
                  // Guest: store locally
                  const tempAddr = {
                    street: selectedAddr.street,
                    city: selectedAddr.city,
                    state: selectedAddr.state,
                    zipCode: selectedAddr.zipCode,
                    apartment: o.addressViewModel.addressDetails.unitNumber,
                    entryCode: o.addressViewModel.addressDetails.entryCode,
                    buildingName: o.addressViewModel.addressDetails.buildingName,
                    latitude: selectedAddr.latitude,
                    longitude: selectedAddr.longitude,
                    deliveryDetails: {
                      dropOffOption: mapFormToDropOff(o.addressViewModel.addressDetails.deliveryPreference),
                      instructions: o.addressViewModel.addressDetails.deliveryNotes || undefined,
                    },
                    storeDeliveryFees: fees,
                  }
                  o.setTemporaryAddress(tempAddr)
                  confirmedAddress = {
                    id: selectedAddr.id,
                    userId: '',
                    businessId: o.currentRestaurant?.id ?? '',
                    ...tempAddr,
                    lastUsed: new Date().toISOString(),
                  }
                }
              } catch (err) {
                safeGetLogger()?.error('Address save/update failed', {
                  domain: 'delivery',
                  error: err instanceof Error ? err.message : String(err),
                })
                // Sheet remains open on 'details' step — user can retry
                return
              }

              if (!confirmedAddress) return

              // Run candidate evaluation after address persistence
              const result = await o.deliveryGuard.evaluateDeliveryCandidates(confirmedAddress, sheetControls.reopen)
              if (!result || !result.passed) {
                if (!result?.isTransientError) sheetControls.close()
                return
              }

              const { candidates } = result

              const singleCandidate = candidates[0]
              if (candidates.length === 1 && singleCandidate) {
                // Single location — auto-commit
                const locationId = singleCandidate.id
                o.setOrderType('Delivery')
                const deliveryState = useDeliveryStore.getState()
                const storedAddress = deliveryState.savedAddresses.find(a => a.id === confirmedAddress.id)
                const fee = storedAddress?.storeDeliveryFees?.[locationId] ?? confirmedAddress.storeDeliveryFees?.[locationId] ?? null
                if (fee !== null) useCartStore.getState().setDeliveryFee(fee)
                const matchedLocation = o.locationDataList.find((l) => l.id === locationId)
                if (matchedLocation) o.handleLocationSelect(matchedLocation)
                o.resetAddressFlowState()
                o.setAddressWasSelected(true)
              } else {
                // Multiple locations — go to location step and fetch progressively
                o.setAddressFlowStep('location')
                o.deliveryGuard.fetchRemainingFeesProgressively(confirmedAddress, candidates).catch(() => {})
              }
            }}
            testID="address-details-content"
          />
        )}

        {o.addressFlowStep === 'location' && (
          <SheetContentStoreLocation
            locations={o.locationDataList}
            onLocationSelect={(location) => {
              o.setOrderType('Delivery')
              o.handleLocationSelect(location)
              o.resetAddressFlowState()
              o.setAddressWasSelected(true)
            }}
            selectedLocationId={o.storeLocation?.id}
            heading="Store Location"
            testID="delivery-location-content"
          />
        )}
      </Sheet>

      {/* Authentication Sheet */}
      <Sheet
        isOpen={o.isAuthSheetOpen}
        onClose={o.closeAuthSheet}
        title="Sign In"
        testID="auth-sheet"
      >
        {/* Sign In with Phone Screen */}
        {o.vm.currentFlow === 'signin-phone' && (
          <SheetContentSignInPhone
            phoneNumber={o.vm.phoneNumber}
            onPhoneNumberChange={o.vm.setPhoneNumber}
            // Showcase has no backend: let the lead type a number, then show the
            // book-a-call nudge instead of sending a real OTP. The ViewModel's
            // signInWithPhone() is untouched for real tenants.
            onContinue={() => {
              if (showcaseBlocked()) return
              o.vm.signInWithPhone()
            }}
            onPasswordSignIn={o.vm.switchToPasswordSignIn}
            showPasswordSignIn={!isShowcase()}
            continueDisabled={!o.vm.canContinue}
            loading={o.vm.loading}
            testID="auth-phone"
          />
        )}

        {/* Enter SMS Code Screen */}
        {o.vm.currentFlow === 'enter-sms-code' && (
          <SheetContentEnterSmsCode
            smsCode={o.vm.smsCode}
            onSmsCodeChange={o.vm.setSmsCode}
            onConfirm={o.vm.verifySmsCode}
            onResendCode={o.vm.resendSmsCode}
            onCancel={o.vm.cancelSmsFlow}
            confirmDisabled={!o.vm.canContinue}
            resendDisabled={!o.vm.canResendSms}
            loading={o.vm.loading}
            resendCountdown={o.vm.smsResendCountdown}
            showCountdown={o.vm.smsResendCountdown > 0}
            testID="auth-sms-code"
          />
        )}

        {/* Sign In with Password Screen */}
        {o.vm.currentFlow === 'signin-password' && (
          <SheetContentSignInPassword
            email={o.vm.email}
            onEmailChange={o.vm.setEmail}
            password={o.vm.password}
            onPasswordChange={o.vm.setPassword}
            onSignIn={o.vm.signInWithPassword}
            onUsePhone={o.vm.switchToPhoneSignIn}
            onResetPassword={o.vm.switchToResetPassword}
            signInDisabled={!o.vm.canContinue}
            loading={o.vm.loading}
            errorMessage={o.vm.errors.signin}
            testID="auth-password"
          />
        )}

        {/* Reset Password Screen */}
        {o.vm.currentFlow === 'reset-password' && (
          <SheetContentResetPassword
            email={o.vm.email}
            onEmailChange={o.vm.setEmail}
            onSendInstructions={() => {
              // A showcase tap must never fire a REAL password-reset email.
              if (showcaseBlocked()) return
              o.vm.sendPasswordResetInstructions()
            }}
            onCancel={o.vm.cancelPasswordReset}
            sendDisabled={!o.vm.canContinue}
            loading={o.vm.loading}
            testID="auth-reset-password"
          />
        )}

        {/* Set New Password Screen — entered via Supabase password-recovery email link */}
        {o.vm.currentFlow === 'set-new-password' && (
          <SheetContentSetNewPassword
            newPassword={o.vm.newPassword}
            confirmPassword={o.vm.confirmPassword}
            onNewPasswordChange={o.vm.setNewPassword}
            onConfirmPasswordChange={o.vm.setConfirmPassword}
            onSave={o.vm.setNewPasswordWithRecoverySession}
            onCancel={o.closeAuthSheet}
            saveDisabled={!o.vm.canContinue}
            loading={o.vm.loading}
            errorMessage={o.vm.errors.setNewPassword}
            testID="auth-set-new-password"
          />
        )}

        {/* Sign Up Screen - profile completion after OTP verification */}
        {o.vm.currentFlow === 'signup' && (
          <SheetContentSignUp
            accountInformation={o.vm.accountInformation}
            onAccountInformationChange={(_fieldName, _fieldValue, allValues) => o.vm.setAccountInformation(allValues)}
            onSignUp={() => {
              if (showcaseBlocked()) return
              o.vm.completeProfile()
            }}
            onSignInInstead={o.vm.switchToSignInInstead}
            signUpDisabled={!o.vm.canContinue}
            loading={o.vm.loading}
            testID="auth-sign-up"
          />
        )}
      </Sheet>

      {/* Order Tracking Sheet */}
      {o.isTrackingSheetOpen && o.activeOrder && (
        <Sheet
          isOpen={o.isTrackingSheetOpen}
          onClose={() => o.setTrackingSheetOpen(false)}
          title={o.activeOrder.isDelivery ? 'Delivery Status' : 'Pickup Status'}
          testID="order-tracking-sheet"
        >
          {o.activeOrder.isDelivery ? (
            <SheetContentDeliveryOrderStatus
              deliveryTime={(() => {
                // Prefer tracking status times, fall back to order's estimated times
                const dropoff = o.activeOrderTrackingStatus?.deliveryTimeRange?.startTime
                  || o.activeOrder.dropoffTimeEstimated
                if (!dropoff) return undefined
                const start = new Date(dropoff)
                if (isNaN(start.getTime())) return undefined
                const end = o.activeOrderTrackingStatus?.deliveryTimeRange?.endTime
                  ? new Date(o.activeOrderTrackingStatus.deliveryTimeRange.endTime)
                  : new Date(start.getTime() + 10 * 60 * 1000)
                const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                return { startTime: fmt(start), endTime: fmt(end) }
              })()}
              orderStatusProps={{
                orderStatus: mapOrderStateToStatusFlow(
                  o.activeOrderTrackingStatus?.currentState || o.activeOrder.status,
                  o.activeOrder.isDelivery,
                ),
              }}
              mapConfig={{
                customContent: o.activeOrderMapLocation ? (
                  <MapOrderTracking
                    variant="delivery"
                    restaurantLocation={o.activeOrderMapLocation}
                    deliveryAddress={
                      o.activeOrder?.deliveryAddress
                        ? {
                            lat: o.activeOrder.deliveryAddress.latitude || 0,
                            lng: o.activeOrder.deliveryAddress.longitude || 0,
                            name: o.activeOrder.deliveryAddress.street,
                          }
                        : undefined
                    }
                    driverLocation={
                      o.activeOrderDriverLocation
                        ? { lat: o.activeOrderDriverLocation.latitude, lng: o.activeOrderDriverLocation.longitude }
                        : undefined
                    }
                    orderState={o.activeOrderTrackingStatus?.currentState || o.activeOrder?.status}
                    testID="tracking-delivery-map"
                  />
                ) : undefined,
              }}
              onContactPress={o.handleContactRestaurant}
              testID="tracking-delivery-status"
            />
          ) : (
            <SheetContentPickupOrderStatus
              pickupTime={(() => {
                const raw = o.activeOrder.pickupTimeEstimated
                  || o.activeOrderTrackingStatus?.estimatedCompletionTime
                if (!raw) return undefined
                const fmt = (d: Date) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                const start = new Date(raw)
                const end = new Date(start.getTime() + 10 * 60 * 1000)
                return `${fmt(start)} - ${fmt(end)}`
              })()}
              orderStatusProps={{
                orderStatus: mapOrderStateToStatusFlow(
                  o.activeOrderTrackingStatus?.currentState || o.activeOrder.status,
                  o.activeOrder.isDelivery,
                ),
              }}
              onGetDirections={() => {
                if (o.currentRestaurant) {
                  const loc = getLocationForOrder(o.activeOrder!, o.currentRestaurant, o.storeLocation ?? undefined)
                  if (loc.latitude && loc.longitude) {
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${loc.latitude},${loc.longitude}`,
                      '_blank',
                    )
                  } else if (loc.address) {
                    window.open(
                      `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                        `${loc.address}, ${loc.city}, ${loc.state} ${loc.zipCode}`
                      )}`,
                      '_blank',
                    )
                  }
                }
              }}
              mapProps={{
                showMap: true,
                children: o.activeOrderMapLocation ? (
                  <MapOrderTracking
                    variant="pickup"
                    restaurantLocation={o.activeOrderMapLocation}
                    testID="tracking-pickup-map"
                  />
                ) : undefined,
              }}
              onContactRestaurant={o.handleContactRestaurant}
              testID="tracking-pickup-status"
            />
          )}
        </Sheet>
      )}

      {/* Favorite Product Sheet */}
      <Sheet
        isOpen={favoriteSheet.isOpen}
        onClose={favoriteSheet.onClose}
        title="Favorite Product"
        testID="favorite-product-sheet"
      >
        {favoriteSheet.sheetProps && (
          <SheetContentFavoriteProduct
            {...favoriteSheet.sheetProps}
            testID="favorite-product-sheet-content"
          />
        )}
      </Sheet>

      {/* Reorder Product Sheet */}
      <Sheet
        isOpen={reorderSheet.isOpen}
        onClose={reorderSheet.onClose}
        title="Re-Order Product"
        testID="reorder-product-sheet"
      >
        {reorderSheet.sheetProps && (
          <SheetContentReorderProduct
            {...reorderSheet.sheetProps}
            testID="reorder-product-sheet-content"
          />
        )}
      </Sheet>

      {/* Delivery availability alert */}
      <Alert {...o.deliveryGuard.alertProps} testID="delivery-availability-alert" />

      {/* Contact restaurant alert */}
      <Alert
        {...o.contactAlert.alertProps}
        title={o.contactAlert.alertProps.title ?? ''}
        description={o.contactAlert.alertProps.description ?? ''}
        primaryAction={o.contactAlert.alertProps.primaryAction ?? { text: '', onPress: () => {} }}
      />

      {/* Offer warning alerts */}
      <Alert
        {...o.offerAlert.alertProps}
        title={o.offerAlert.alertProps.title ?? ''}
        description={o.offerAlert.alertProps.description ?? ''}
        primaryAction={o.offerAlert.alertProps.primaryAction ?? { text: '', onPress: () => {} }}
      />
    </>
  )
}
